/**
 * Action Configurator Component
 * Configures actions that execute when a rule matches
 * Phase 6: Only ValidationError is fully supported
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import Warning from '@mui/icons-material/Warning';
import type { RuleActionDto } from '@/types/core/Rules';

type ActionType = 'ValidationError' | 'SetField' | 'TriggerCommand' | 'TriggerWorkflow';

interface ActionConfiguratorProps {
  /** Current actions */
  value: RuleActionDto[];
  /** Callback when actions change */
  onChange: (actions: RuleActionDto[]) => void;
  /** Error message */
  error?: string;
  /** Disable editing */
  disabled?: boolean;
}

/**
 * Actions that are fully supported in Phase 6
 */
const SUPPORTED_ACTIONS: ActionType[] = ['ValidationError'];

/**
 * Actions coming in future phases
 */
const COMING_SOON_ACTIONS: ActionType[] = ['SetField', 'TriggerCommand', 'TriggerWorkflow'];

/**
 * Default parameters for each action type
 */
function getDefaultParameters(type: ActionType): Record<string, any> {
  switch (type) {
    case 'ValidationError':
      // The message and severity a failing rule raises live on the RULE (Error Handling) —
      // the engine reads them from there. The action is the marker that the rule is a
      // validation, not a decision.
      return {};
    case 'SetField':
      return {
        fieldPath: '',
        value: '',
      };
    case 'TriggerCommand':
      return {
        commandType: '',
      };
    case 'TriggerWorkflow':
      return {
        workflowKey: '',
      };
    default:
      return {};
  }
}

/**
 * Debounced text field that uses local state to prevent character loss during rapid typing.
 * Syncs with parent on blur or after typing stops.
 */
interface DebouncedTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  transform?: (value: string) => string;
  label: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
}

function DebouncedTextField({
  value,
  onChange,
  transform,
  label,
  placeholder,
  helperText,
  required,
  disabled,
  multiline,
  rows,
}: DebouncedTextFieldProps) {
  const [localValue, setLocalValue] = useState(value);

  // Sync local state when external value changes (e.g., action type change)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = transform ? transform(e.target.value) : e.target.value;
    setLocalValue(newValue);
  };

  const handleBlur = () => {
    // Only trigger onChange if value actually changed
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      helperText={helperText}
      required={required}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      slotProps={{ input: { readOnly: disabled } }}
    />
  );
}

interface ActionRowProps {
  action: RuleActionDto;
  index: number;
  onChange: (action: RuleActionDto) => void;
  onRemove: () => void;
  disabled?: boolean;
}

function ActionRow({ action, index, onChange, onRemove, disabled }: ActionRowProps) {
  const { t } = useTranslation();
  const isComingSoon = COMING_SOON_ACTIONS.includes(action.type as ActionType);

  const handleTypeChange = (newType: ActionType) => {
    onChange({
      type: newType,
      parameters: getDefaultParameters(newType),
    });
  };

  const handleParameterChange = (key: string, value: any) => {
    onChange({
      ...action,
      parameters: {
        ...action.parameters,
        [key]: value,
      },
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Action {index + 1}
            </Typography>
            {isComingSoon && (
              <Chip
                icon={<Warning />}
                label="Coming soon"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
          {!disabled && (
            <IconButton size="small" onClick={onRemove} color="error">
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Action Type */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={disabled}>
          <InputLabel>Action Type</InputLabel>
          <Select
            value={action.type}
            label="Action Type"
            onChange={(e) => handleTypeChange(e.target.value as ActionType)}
            readOnly={disabled}
          >
            {SUPPORTED_ACTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
            {COMING_SOON_ACTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {type} (Coming soon)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Coming soon warning */}
        {isComingSoon && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This action type will be executed when backend support is complete.
            You can configure it now and it will automatically work when ready.
          </Alert>
        )}

        {/* ValidationError: no parameters. The message and the severity (Error blocks, Warning
            asks) are the RULE's Error Handling fields, which is what the engine reads — a
            per-action message was only a fallback when that field was empty, and the action's
            code/field path were never read at all. One place to author, one place to read. */}
        {action.type === 'ValidationError' && (
          <Alert severity="info">{t('businessRules.actions.validationErrorInfo')}</Alert>
        )}

        {/* SetField Parameters */}
        {action.type === 'SetField' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DebouncedTextField
              label="Field Path"
              value={action.parameters.fieldPath || ''}
              onChange={(value) => handleParameterChange('fieldPath', value)}
              placeholder="e.g., Order.priority"
              required={!disabled}
              disabled={disabled}
            />
            <DebouncedTextField
              label="Value"
              value={action.parameters.value || ''}
              onChange={(value) => handleParameterChange('value', value)}
              placeholder="Value to set"
              required={!disabled}
              disabled={disabled}
            />
          </Box>
        )}

        {/* TriggerCommand Parameters */}
        {action.type === 'TriggerCommand' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DebouncedTextField
              label="Command Type"
              value={action.parameters.commandType || ''}
              onChange={(value) => handleParameterChange('commandType', value)}
              placeholder="e.g., SendNotification"
              required={!disabled}
              disabled={disabled}
            />
          </Box>
        )}

        {/* TriggerWorkflow Parameters */}
        {action.type === 'TriggerWorkflow' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DebouncedTextField
              label="Workflow Key"
              value={action.parameters.workflowKey || ''}
              onChange={(value) => handleParameterChange('workflowKey', value)}
              placeholder="e.g., ApprovalWorkflow"
              required={!disabled}
              disabled={disabled}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export function ActionConfigurator({ value, onChange, error, disabled }: ActionConfiguratorProps) {
  const handleAddAction = () => {
    const newAction: RuleActionDto = {
      type: 'ValidationError',
      parameters: getDefaultParameters('ValidationError'),
    };
    onChange([...value, newAction]);
  };

  const handleUpdateAction = (index: number, action: RuleActionDto) => {
    const newActions = [...value];
    newActions[index] = action;
    onChange(newActions);
  };

  const handleRemoveAction = (index: number) => {
    const newActions = value.filter((_, i) => i !== index);
    onChange(newActions);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {value.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {disabled
            ? 'No actions configured for this rule.'
            : 'No actions configured. Add at least one action to define what happens when the rule matches.'}
        </Alert>
      ) : (
        value.map((action, index) => (
          <ActionRow
            key={index}
            action={action}
            index={index}
            onChange={(updated) => handleUpdateAction(index, updated)}
            onRemove={() => handleRemoveAction(index)}
            disabled={disabled}
          />
        ))
      )}

      {!disabled && (
        <Button
          startIcon={<Add />}
          onClick={handleAddAction}
          variant="outlined"
          size="small"
          sx={{ mt: 1 }}
        >
          Add Action
        </Button>
      )}
    </Box>
  );
}

export default ActionConfigurator;
